import { projects, workExperience } from './profile';

/**
 * What the portfolio pages show, and in which order. Content lives in projects.json /
 * workExperience.json / Contentful; this file only picks and arranges it.
 */

/** The year the career started (for "14+ years") */
export const CAREER_START = 2012;

export const CV_URL = '/profile/CV_Shaojiang_Cai.pdf';

/** Projects featured on the home page, biggest first */
export const FEATURED_PROJECTS = ['yingyu-io', 'ownkeep', 'myex-ai', 'vvs', 'pano-ai-360'];

/**
 * Logos for the "teams I've helped" band, by the names in the site copy. `cover` logos come on
 * their own colored square; `contain` ones are a bare mark that sits on a plain tile.
 */
export const CLIENT_LOGOS: Record<string, { src: string; fit: 'cover' | 'contain' }> = {
  Binance: { src: '/projects/binance.png', fit: 'contain' },
  Bitfinex: { src: '/projects/bitfinex-logo.png', fit: 'cover' },
  CronosLabs: { src: '/projects/cronoslabs-logo.png', fit: 'cover' },
  'Pano AI': { src: '/projects/pano-logo.jpeg', fit: 'cover' },
  Toptal: { src: '/projects/toptal-logo.png', fit: 'contain' },
  'EMBL-EBI': { src: '/projects/ebi-logo.png', fit: 'cover' },
  RingCentral: { src: '/projects/rc-logo.png', fit: 'cover' },
};

/** Illustrated poses of the avatar, used as friendly section art */
export const POSES = {
  hero: '/profile/poses/hero.webp',
  glasses: '/profile/poses/glasses.webp',
  thinking: '/profile/poses/thinking.webp',
  necklace: '/profile/poses/necklace.webp',
} as const;

export type Pose = keyof typeof POSES;

export type ShowcaseVisual =
  'image' | 'audio' | 'border' | 'dashes' | 'form' | 'site' | 'colors' | 'base64';

export interface Showcase {
  slug: string;
  href: string;
  visual: ShowcaseVisual;
  image?: string;
  tech: string;
}

export const SHOWCASES: Showcase[] = [
  {
    slug: 'panorama',
    href: '/showcases/panorama',
    visual: 'image',
    image: '/images/p/3.jpeg',
    tech: 'Canvas',
  },
  { slug: 'audio-player', href: '/showcases/audio-player', visual: 'audio', tech: 'Web Audio' },
  { slug: 'fancy-border', href: '/showcases/fancy-border', visual: 'border', tech: 'CSS' },
  {
    slug: 'flowing-dashed-lines',
    href: '/showcases/flowing-dashed-lines',
    visual: 'dashes',
    tech: 'SVG',
  },
  { slug: 'form', href: '/showcases/form', visual: 'form', tech: 'React Hook Form' },
  { slug: 'enterprise', href: '/showcases/enterprise', visual: 'site', tech: 'HTML · CSS' },
];

export type ToolSlug = 'color-picker' | 'svg2base64';

export const TOOLS: { slug: ToolSlug; href: string }[] = [
  { slug: 'color-picker', href: '/tools/color-picker' },
  { slug: 'svg2base64', href: '/tools/svg2base64' },
];

export const visibleProjects = projects.filter((project) => !project.hidden);

export const featuredProjects = FEATURED_PROJECTS.map((slug) =>
  visibleProjects.find((project) => project.slug === slug),
).filter((project) => project !== undefined);

/** Parses the data files' dates ("2026/05", "2023-02-01") to a year */
export const yearOf = (date: string | undefined) => {
  const match = date?.match(/\d{4}/);
  return match ? Number(match[0]) : undefined;
};

/** "2024 — 26", "2026 —", "2016 — 17" */
export function yearRange(start?: string, end?: string, now = '') {
  const from = yearOf(start);
  const to = yearOf(end);
  if (!from) return '';
  if (!to) return now ? `${from} — ${now}` : `${from} —`;
  if (to === from) return String(from);
  return `${from} — ${String(to).slice(2)}`;
}

/** Years of the career so far ("14") */
export const careerYears = () => new Date().getFullYear() - CAREER_START;

/** Oldest and newest project years, for "22 projects · 2011 — 2026" */
export const projectYears = () => {
  const years = visibleProjects.flatMap((p) => [yearOf(p.startDate), yearOf(p.endDate)]);
  const known = years.filter((year): year is number => year !== undefined);
  return { from: Math.min(...known), to: Math.max(...known) };
};

/** Work history for the timeline, newest first */
export const chapters = workExperience
  .filter((job) => !job.hidden)
  .map((job) => ({
    company: job.company,
    title: job.title,
    years: yearRange(job.startDate, job.endDate),
    projects: job.projects,
  }));

/** How long a project ran: months under two years, otherwise years */
export function projectDuration(
  project: { startDate?: string; endDate: string },
  today = new Date(),
) {
  const start = project.startDate ? new Date(project.startDate) : undefined;
  if (!start || Number.isNaN(start.getTime())) return undefined;
  const end = project.endDate ? new Date(project.endDate) : today;
  const months = Math.max(
    1,
    (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth(),
  );
  return months < 24
    ? { value: months, unit: 'months' as const }
    : { value: Math.round(months / 12), unit: 'years' as const };
}

/** "Yingyu" + ".io": the part after the last dot is highlighted in titles */
export function splitName(name: string) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
}
